import GiftLoadError from '@activities/gift/_shared/components/GiftLoadError';
import BlankEtc from '@components/blank/BlankEtc';
import InfinityScrollTrigger from '@components/InfinityScrollTrigger';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import GiftBoxItemSkeletons from './components/GiftBoxItemSkeletons';
import ReceivedGiftItem from './components/ReceivedGiftItem';
import useReceivedGiftBoxes from './hooks/useReceivedGiftBoxes';

function ReceivedGiftBoxFragment() {
    const { t } = useTranslation('gift');
    const [availableOnly, setAvailableOnly] = useState(false);
    const {
        gifts, isEmpty, isFetching, fetchNextPage, hasNextPage, isError, isFetchNextPageError, refetch,
    } = useReceivedGiftBoxes(availableOnly);

    return (
        <Section aria-label={t('giftBox.received')}>
            <FilterRow>
                <Text as='span'>{t('giftBox.availableOnly')}</Text>
                <Toggle type='button' role='switch' aria-checked={availableOnly}
                    aria-label={t('giftBox.availableOnly')}
                    onClick={() => setAvailableOnly(!availableOnly)} />
            </FilterRow>
            {isError && (
                <GiftLoadError message={t('giftBox.loadError')} isFetching={isFetching}
                    onRetry={() => (isFetchNextPageError ? fetchNextPage() : refetch())} />
            )}
            {isEmpty && !isFetching && !isError ? (
                <BlankEtc>
                    <Text size='body-short' color={colors.textSecondary} fontWeight='regular' align='center'>
                        {t('blank.receiveSubtitle')}<br />{t('blank.receive')}
                    </Text>
                </BlankEtc>
            ) : (
                <List>
                    {gifts.map((gift) => (
                        <ReceivedGiftItem key={gift.id} gift={gift} />
                    ))}
                    {isFetching && <GiftBoxItemSkeletons count={gifts.length ? 1 : 5} />}
                    {hasNextPage && !isFetching && !isError && (
                        <InfinityScrollTrigger trigger={() => fetchNextPage()} />
                    )}
                </List>
            )}
        </Section>
    );
}

const Section = styled.section`
    width: 100%;
    padding-block: var(--spacing-4);
`;

const FilterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-2-5) var(--spacing-4);
`;

const Toggle = styled.button`
    flex-shrink: 0;
    width: 44px;
    height: 24px;
    padding: var(--spacing-0-5);
    border: 0;
    border-radius: 12px;
    background: ${colors.gray2};
    &::after {
        content: '';
        display: block;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: ${colors.gray5};
        transition: transform 100ms;
    }
    &[aria-checked='true'] {
        background: var(--color-secondary);
        &::after { background: ${colors.primary}; transform: translateX(20px); }
    }
    &:focus-visible { outline: 2px solid ${colors.primary}; outline-offset: 2px; }
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacing-6);
    padding: var(--spacing-4) var(--spacing-4) var(--spacing-10);
`;

export default ReceivedGiftBoxFragment;
